import type { address } from '@package/drizzle/schema'

type SelectAddress = typeof address.$inferSelect

/**
 * Formats an address object into a single comma-separated string.
 * Follows Indonesian address hierarchy: street → village → district → regency → province → postal code → country.
 *
 * @param addressData - The address object containing hierarchical address components
 * @returns A formatted address string with components separated by commas. Empty/null fields are omitted.
 *
 * @remarks
 * - The first line (addressLineOne) is always included
 * - Optional fields are only included if they have values
 * - Components are ordered from most specific (street) to least specific (country)
 * - This format follows Indonesian administrative divisions
 *
 * @example
 * // Full address with all fields
 * const address = {
 *   addressLineOne: 'Jl. Sudirman No. 123',
 *   addressLineTwo: 'Komplek ABC',
 *   village: 'Kelurahan XYZ',
 *   district: 'Kecamatan Central',
 *   regency: 'Kota Jakarta',
 *   province: 'DKI Jakarta',
 *   postalCode: '12345',
 *   country: 'Indonesia'
 * }
 * formatAddressToString(address)
 * // Returns: "Jl. Sudirman No. 123, Komplek ABC, Kelurahan XYZ, Kecamatan Central, Kota Jakarta, DKI Jakarta, 12345, Indonesia"
 *
 * @example
 * // Minimal address (only required field)
 * const minimalAddress = {
 *   addressLineOne: 'Jl. Merdeka No. 45',
 *   addressLineTwo: null,
 *   village: null,
 *   // ... other fields null/undefined
 * }
 * formatAddressToString(minimalAddress)
 * // Returns: "Jl. Merdeka No. 45"
 */
export function formatAddressToString(addressData: SelectAddress): string {
  // Define the address components in hierarchical order
  const addressComponents = [
    addressData.addressLineOne,
    addressData.addressLineTwo,
    addressData.village,
    addressData.district,
    addressData.regency,
    addressData.province,
    addressData.postalCode,
    addressData.country,
  ]

  // Filter out null/undefined/empty values and join with commas
  return addressComponents.filter((component) => component != null && component !== '').join(', ')
}
